import './styles.css';

import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 10_000,
      refetchOnWindowFocus: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');

/**
 * Auth0 SPA config — read at build time from VITE_AUTH0_*. The mock-Auth0
 * dev path (VITE_ENABLE_MOCK_AUTH0=true) does not exercise the Auth0 SDK,
 * but the provider must still wrap the tree because `useAuth0()` is called
 * unconditionally inside `AuthProvider`. Empty strings are tolerated by
 * `@auth0/auth0-react`'s constructor — the SDK only contacts Auth0 when
 * `loginWithRedirect()` is actually invoked, so a mock-mode build with
 * placeholder values is safe.
 */
const auth0Domain = String(import.meta.env['VITE_AUTH0_DOMAIN'] ?? '');
const auth0ClientId = String(import.meta.env['VITE_AUTH0_CLIENT_ID'] ?? '');
const auth0Audience = String(import.meta.env['VITE_AUTH0_AUDIENCE'] ?? '');
const auth0RedirectUri = String(
  import.meta.env['VITE_AUTH0_REDIRECT_URI'] ?? window.location.origin,
);

createRoot(container).render(
  <StrictMode>
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      authorizationParams={{
        audience: auth0Audience,
        redirect_uri: auth0RedirectUri,
      }}
      // Rotating refresh tokens (held in the SDK's in-memory cache, never
      // localStorage) — required for `getAccessTokenSilently()` to renew
      // reliably in browsers that block third-party cookies. Powers the
      // 401 silent-renew in `AuthProvider`'s fetcher.
      useRefreshTokens
      cacheLocation="memory"
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </Auth0Provider>
  </StrictMode>,
);
